import { z } from "zod";
import i18next from "i18next";
import {
  FIELD_END_DATE_LESS_THAN_NOW,
  FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
  FIELD_REQUIRED,
  START_DATE_AND_END_DATE_VALIDATION,
  FIELD_META_PLACEMENT_FACEBOOK_PROFILE_FEED_REQUIRED,
  FIELD_META_PLACEMENT_FACEBOOK_MARKETPLACE_REQUIRED,
  FIELD_META_PLACEMENT_MESSENGER_INBOX_REQUIRED,
  FIELD_META_PLACEMENT_FACEBOOK_NOTIFICATIONS_REQUIRED,
  FIELD_META_PLACEMENT_AUDIENCE_NETWORK_NATIVE_BANNER_INTERSTITIAL_REQUIRED,
  FIELD_META_PLACEMENT_AUDIENCE_NETWORK_REWARDED_VIDEOS_REQUIRED,
  FIELD_META_PLACEMENT_ADS_ON_FACEBOOK_REELS_REQUIRED,
  FIELD_META_PLACEMENT_FACEBOOK_INSTREAM_VIDEOS_REQUIRED,
  FIELD_META_PLACEMENT_FACEBOOK_SEARCH_RESULTS_REQUIRED,
  FIELD_META_PLACEMENT_INSTAGRAM_PROFILE_FEED_REQUIRED,
  FIELD_META_PLACEMENT_INSTAGRAM_SEARCH_RESULTS_REQUIRED,
  FIELD_META_PLACEMENT_INSTAGRAM_EXPLORE_REQUIRED,
  FIELD_META_PLACEMENT_INSTAGRAM_EXPLORE_HOME_REQUIRED,
  FIELD_META_PLACEMENT_INSTAGRAM_PROFILE_REELS_REQUIRED,
  FIELD_NUMBER_NEGATIVE,
  FIELD_ESTIMATION_ID_REQUIRED,
  FIELD_START_DATE_LESS_THAN_NOW,
  FIELD_NUMBER_LESS_THAN,
  FIELD_NUMBER_GREATER_THAN,
  FIELD_IS_DYNAMIC_CREATIVE_ONLY_ONE_AD,
  FIELD_SCHEDULE_REQUIRED_WHEN_CAMPAIGN_BUDGET_OTHERS_HAVE,
  FIELD_BID_GOAL_REQUIRED_WHEN_CAMPAIGN_BUDGET_OTHERS_HAVE,
} from "../../../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WHATSAPP_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_APP_PROMOTION_OBJECTIVE,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_OBJECTIVES_META_WITH_DESTINATION_TYPE,
  ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META,
  ADS_CAMPAIGN_STATUS_DRAFT,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  ADS_CAMPAIGN_BUYING_TYPE_RESERVED_OPTION,
  META,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_TRACKING_ID,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_OFFLINE_TRACKING_ID,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_APP_ID,
  ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_IG_REQUIRED,
  getIsMetaSalesWhatsappDestination,
} from "../../../../../utils/constants";
import { isBefore } from "date-fns";
import {
  getIsRequiredProductSetID,
  getMinimumBudget,
} from "../../../../../services/adsCampaigns";
import { getIntegrationKey } from "../../../../../actions/getters";
import _ from "lodash";

const metaAdGroupBidGoalHasValue = (bidGoal) => {
  if (
    bidGoal === null ||
    bidGoal === undefined ||
    bidGoal === "" ||
    bidGoal === 0 ||
    bidGoal === "0"
  ) {
    return false;
  }
  const n = Number(bidGoal);
  return !Number.isNaN(n) && n > 0;
};

export const adGroupMetaBaseSchema = z.object({
  ads: z.array(z.any()).optional(),
  platform: z.literal(META),
  typeBudget: z.any().optional(),
  budget: z.any().optional().default(0),
  minBudget: z.any().optional().default(0),
  maxBudget: z.any().optional().default(0),
  budgetLimitsMode: z.any().optional(),
  startDate: z.any().optional(),
  endDate: z.any().optional(),
  customAudiences: z.array(z.any()).optional(),
  excludedCustomAudiences: z.array(z.any()).optional(),
  ageRange: z.tuple(
    [
      z.number().min(18, i18next.t(FIELD_REQUIRED)),
      z.number().max(65, i18next.t(FIELD_REQUIRED)),
    ],
    {
      required_error: i18next.t(FIELD_REQUIRED),
    }
  ),
  genders: z.object({
    male: z.boolean(),
    female: z.boolean(),
  }),
  locations: z.array(z.any()).optional(),
  excludedLocations: z.any().optional(),
  interests: z.array(z.any()).optional(),
  behaviors: z.array(z.any()).optional(),
  demographics: z.array(z.any()).optional(),

  destinationType: z.any().optional(),
  whatsappBusinessNumber: z.any().optional(),
  trackingCustomEventID: z.any().optional(),
  trackingID: z.any().optional(),
  trackingOfflineID: z.any().optional(),
  catalogID: z.any().optional(),
  productSetID: z.any().optional(),
  productSetAudience: z.any().optional(),

  appID: z.any().optional(),

  placements: z.any().optional(),

  bidGoal: z.any().optional(),
  optimizationGoal: z.any().optional(),
  attributionWindow: z.any().optional(),
  attributionEngagedView: z.any().optional(),
  estimationID: z.any().optional(),
  frequency: z.any().optional(),
  schedule: z.any().optional(),

  isDynamicCreative: z.boolean().optional().default(false),
});

const verifyMetaBudget = ({
  typeBudget,
  budget,
  integrations,
  integrationKey,
  integrationID,
  adAccountID,
  startDate,
  endDate,
  currencyTaxes,
  buyingType,
}) => {
  if (buyingType === ADS_CAMPAIGN_BUYING_TYPE_RESERVED_OPTION) {
    return { ok: true };
  }

  const integrationsMeta = integrations[integrationKey] || {};
  const integration = integrationsMeta?.[integrationID];

  if (!integration) {
    return { ok: true };
  }

  const currency = integration?.businessAccounts?.[adAccountID]?.currency;

  if (!currency) {
    return { ok: true };
  }
  const currencyTax = currencyTaxes?.[currency] || 1;

  const budgetMinimum = getMinimumBudget({
    platform: META,
    typeBudget,
    startDateTime: startDate,
    endDateTime: endDate,
    currency,
    currencyTax,
  });

  const numericBudget = Number(budget || 0);

  if (numericBudget < budgetMinimum) {
    return {
      ok: false,
      message: FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
      value: budgetMinimum,
      currency,
    };
  }

  return { ok: true };
};

export const validateAdGroupMeta = ({
  campaign,
  integrations,
  adGroup,
  indexAdGroup,
  ctx,
  currencyTaxes,
}) => {
  const statusCampaign = campaign.states?.[META];
  const isStatusDraft = statusCampaign === ADS_CAMPAIGN_STATUS_DRAFT;
  const platformData = campaign.platformsData?.[META];
  const objective = platformData?.objective;
  const destinationType = adGroup.destinationType;
  const needsTracking =
    ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META.includes(objective);
  const isWebsite =
    destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION;
  const isSalesWhatsapp = getIsMetaSalesWhatsappDestination({
    objective,
    destinationType,
  });
  const useCampaignBudget = platformData?.useCampaignBudget;
  const typeBudgetCampaign = platformData?.typeBudget;

  const showAppPromotion =
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_APP_PROMOTION_OBJECTIVE.includes(
      destinationType
    );

  const isRequiredProductSetID = getIsRequiredProductSetID({
    objective,
    isCatalogCampaign: platformData?.isCatalogCampaign,
  });

  const basicPaths = ["adsGroups", indexAdGroup];

  if (adGroup.genders) {
    const { male = false, female = false } = adGroup.genders;
    if (!male && !female) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "genders"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  } else {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "genders"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (
    (!adGroup.locations || adGroup.locations.length === 0) &&
    _.isEmpty(adGroup.customAudiences)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "locations"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (adGroup.bidGoal && adGroup.bidGoal < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "bidGoal"],
      message: i18next.t(FIELD_NUMBER_NEGATIVE),
    });
  }

  if (
    ((needsTracking && isWebsite) ||
      platformData?.isCatalogCampaign ||
      (showAppPromotion &&
        objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION)) &&
    !adGroup.trackingCustomEventID
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "trackingCustomEventID"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  const isTotalBudget =
    adGroup.typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION ||
    typeBudgetCampaign === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION;

  if (!adGroup.startDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "startDate"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (isTotalBudget && !adGroup.endDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "endDate"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (adGroup.startDate && adGroup.endDate) {
    const startDate = new Date(adGroup.startDate);
    const endDate = new Date(adGroup.endDate);
    const now = new Date();

    if (endDate.getTime() < now.getTime() && isStatusDraft) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "endDate"],
        message: i18next.t(FIELD_END_DATE_LESS_THAN_NOW),
      });
    }
    if (startDate.getTime() > endDate.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "startDate"],
        message: i18next.t(START_DATE_AND_END_DATE_VALIDATION),
      });
    }
  }

  if (!useCampaignBudget) {
    const numericBudget = Number(adGroup.budget);
    if (isNaN(numericBudget) || !numericBudget) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "budget"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (!adGroup.typeBudget) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "typeBudget"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    const budgetValidation = verifyMetaBudget({
      typeBudget: adGroup.typeBudget,
      budget: adGroup.budget,
      integrations,
      integrationKey: getIntegrationKey(META, true),
      integrationID: platformData?.integrationID,
      adAccountID: platformData?.adAccountID,
      startDate: adGroup.startDate,
      endDate: adGroup.endDate,
      currencyTaxes,
      buyingType: platformData?.buyingType,
    });

    if (!budgetValidation.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "budget"],
        message: i18next.t(budgetValidation.message, {
          value: budgetValidation.value,
          dailyValue: budgetValidation.dailyValue,
          currency: budgetValidation.currency,
        }),
      });
    }
  }

  if (useCampaignBudget) {
    if (adGroup.minBudget && adGroup.minBudget < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "minBudget"],
        message: i18next.t(FIELD_NUMBER_NEGATIVE),
      });
    }
    if (adGroup.maxBudget && adGroup.maxBudget < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "maxBudget"],
        message: i18next.t(FIELD_NUMBER_NEGATIVE),
      });
    }
  }

  // Schedule required: when campaign budget + lifetime + any Meta ad group has schedule
  if (
    useCampaignBudget &&
    typeBudgetCampaign === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION
  ) {
    const adsGroups = campaign.adsGroups || [];
    const hasAnyMetaWithSchedule = adsGroups.some(
      (ag) =>
        ag?.platform === META &&
        Array.isArray(ag?.schedule?.intervals) &&
        ag.schedule.intervals.length > 0
    );

    if (hasAnyMetaWithSchedule) {
      const hasCurrentSchedule =
        Array.isArray(adGroup?.schedule?.intervals) &&
        adGroup.schedule.intervals.length > 0;

      if (!hasCurrentSchedule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "schedule"],
          message: i18next.t(
            FIELD_SCHEDULE_REQUIRED_WHEN_CAMPAIGN_BUDGET_OTHERS_HAVE
          ),
        });
      }
    }
  }

  // Bid goal required: when campaign budget + any Meta ad group has bidGoal
  if (useCampaignBudget) {
    const adsGroupsForBidGoal = campaign.adsGroups || [];
    const hasAnyMetaWithBidGoal = adsGroupsForBidGoal.some(
      (ag) => ag?.platform === META && metaAdGroupBidGoalHasValue(ag?.bidGoal)
    );

    if (hasAnyMetaWithBidGoal && !metaAdGroupBidGoalHasValue(adGroup.bidGoal)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "bidGoal"],
        message: i18next.t(
          FIELD_BID_GOAL_REQUIRED_WHEN_CAMPAIGN_BUDGET_OTHERS_HAVE
        ),
      });
    }
  }

  if (
    ADS_CAMPAIGN_OBJECTIVES_META_WITH_DESTINATION_TYPE.includes(objective) &&
    !destinationType
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "destinationType"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (
    destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_WHATSAPP_OPTION &&
    !adGroup.whatsappBusinessNumber
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "whatsappBusinessNumber"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if ((needsTracking && isWebsite) || isSalesWhatsapp) {
    if (!adGroup.trackingID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "trackingID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  }

  if (
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_TRACKING_ID.includes(
      destinationType
    ) &&
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION
  ) {
    if (!adGroup.trackingID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "trackingID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  }
  if (
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_OFFLINE_TRACKING_ID.includes(
      destinationType
    ) &&
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION
  ) {
    if (!adGroup.trackingOfflineID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "trackingOfflineID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  }
  if (
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_APP_ID.includes(destinationType) &&
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION
  ) {
    if (!adGroup.appID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "appID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  }

  // Validar instagramID si el destinationType lo requiere
  if (
    destinationType &&
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_IG_REQUIRED.includes(
      destinationType
    ) &&
    !platformData?.instagramID
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["platformsData", META, "instagramID"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (
    ((needsTracking && isWebsite) ||
      isSalesWhatsapp ||
      platformData?.isCatalogCampaign) &&
    !adGroup.trackingCustomEventID
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "trackingCustomEventID"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (adGroup.isDynamicCreative) {
    const ads = adGroup.ads || [];
    if (ads.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "ads"],
        message: i18next.t(FIELD_IS_DYNAMIC_CREATIVE_ONLY_ONE_AD),
      });
    }
  }

  if (platformData?.isCatalogCampaign) {
    if (isRequiredProductSetID && !adGroup.productSetID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "productSetID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (adGroup.productSetID) {
      const productSetAudience = adGroup.productSetAudience || {};
      const {
        inclusions = [],
        exclusions = [],
        productSetID,
      } = productSetAudience;

      if ((inclusions?.length > 0 || exclusions?.length > 0) && !productSetID) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "productSetAudience", "productSetID"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
    }
  }

  if (platformData?.objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION) {
    if (!adGroup.appID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "appID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  }

  // RESERVED buying type specific validations
  const buyingType = platformData?.buyingType;
  const isReservedCampaign =
    buyingType === ADS_CAMPAIGN_BUYING_TYPE_RESERVED_OPTION;

  if (
    !isReservedCampaign &&
    objective === ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION
  ) {
    if (!adGroup.frequency?.maxFrequency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "maxFrequency"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (!adGroup.frequency?.intervalDays) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "intervalDays"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (
      adGroup.frequency?.maxFrequency &&
      adGroup.frequency?.maxFrequency < 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "maxFrequency"],
        message: i18next.t(FIELD_NUMBER_LESS_THAN, { value: 1 }),
      });
    }
    if (
      adGroup.frequency?.intervalDays &&
      adGroup.frequency?.intervalDays < 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "intervalDays"],
        message: i18next.t(FIELD_NUMBER_LESS_THAN, { value: 1 }),
      });
    }
    if (
      adGroup.frequency?.maxFrequency &&
      adGroup.frequency?.maxFrequency > 90
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "maxFrequency"],
        message: i18next.t(FIELD_NUMBER_GREATER_THAN, { value: 90 }),
      });
    }
    if (
      adGroup.frequency?.intervalDays &&
      adGroup.frequency?.intervalDays > 90
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "intervalDays"],
        message: i18next.t(FIELD_NUMBER_GREATER_THAN, { value: 90 }),
      });
    }
  }

  if (isReservedCampaign) {
    // 1. locations required for RESERVED campaigns
    if (!adGroup.locations || adGroup.locations.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "locations"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // 2. startDate and endDate required for RESERVED campaigns
    if (!adGroup.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "startDate"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    if (!adGroup.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "endDate"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // 3. estimationID required for RESERVED campaigns
    if (!adGroup.estimationID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "estimationID"],
        message: i18next.t(FIELD_ESTIMATION_ID_REQUIRED),
      });
    }

    // 4. frequency fields required for RESERVED campaigns
    if (!adGroup?.frequency?.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "value"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    if (!adGroup?.frequency?.interval) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "interval"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    if (!adGroup?.frequency?.type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "frequency", "type"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // 5. Date validations for RESERVED campaigns
    if (adGroup.startDate && adGroup.endDate) {
      const startDate = new Date(adGroup.startDate);
      const endDate = new Date(adGroup.endDate);
      const now = new Date();

      // startDate should not be greater than endDate
      if (isBefore(endDate, startDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "startDate"],
          message: i18next.t(START_DATE_AND_END_DATE_VALIDATION),
        });
      }

      // startDate should not be in the past (only if status is DRAFT)
      if (isStatusDraft && isBefore(startDate, now)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "startDate"],
          message: i18next.t(FIELD_START_DATE_LESS_THAN_NOW),
        });
      }
    }
  }

  if (!isReservedCampaign) {
    validateMetaPlacements({
      placements: adGroup.placements,
      ctx,
      basicPaths: [...basicPaths, "placements"],
    });
  }
};

// Placement dependency validation for Meta manual placements
const validateMetaPlacements = ({ placements = {}, ctx, basicPaths }) => {
  if (placements?.type !== "manual") return;

  const has = (arr, key) => Array.isArray(arr) && arr?.includes(key);

  // Facebook dependencies
  if (
    has(placements.facebookPositions, "facebookProfileFeed") &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_FACEBOOK_PROFILE_FEED_REQUIRED),
    });
  }
  if (
    has(placements.facebookPositions, "facebookMarketplace") &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_FACEBOOK_MARKETPLACE_REQUIRED),
    });
  }
  if (
    has(placements.messengerPositions, "messengerInbox") &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_MESSENGER_INBOX_REQUIRED),
    });
  }
  if (
    has(placements.facebookPositions, "facebookNotifications") &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_FACEBOOK_NOTIFICATIONS_REQUIRED),
    });
  }
  if (
    has(
      placements.audienceNetworkPositions,
      "audienceNetworkNativeBannerInterstitial"
    ) &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(
        FIELD_META_PLACEMENT_AUDIENCE_NETWORK_NATIVE_BANNER_INTERSTITIAL_REQUIRED
      ),
    });
  }
  if (
    has(placements.audienceNetworkPositions, "audienceNetworkRewardedVideos") &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(
        FIELD_META_PLACEMENT_AUDIENCE_NETWORK_REWARDED_VIDEOS_REQUIRED
      ),
    });
  }
  if (
    has(placements.facebookPositions, "adsOnFacebookReels") &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_ADS_ON_FACEBOOK_REELS_REQUIRED),
    });
  }
  if (
    has(placements.facebookPositions, "facebookInstreamVideos") &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(
        FIELD_META_PLACEMENT_FACEBOOK_INSTREAM_VIDEOS_REQUIRED
      ),
    });
  }
  if (
    has(placements.facebookPositions, "facebookSearchResults") &&
    !has(placements.facebookPositions, "facebookFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_FACEBOOK_SEARCH_RESULTS_REQUIRED),
    });
  }

  // Instagram dependencies
  // if (
  //   has(placements.facebookPositions, "facebookStories") &&
  //   !has(placements.instagramPositions, "instagramStories")
  // ) {
  //   ctx.addIssue({
  //     code: z.ZodIssueCode.custom,
  //     path: [...basicPaths, "placements"],
  //     message: i18next.t(FIELD_META_PLACEMENT_INSTAGRAM_STORIES_REQUIRED),
  //   });
  // }
  if (
    has(placements.instagramPositions, "instagramProfileFeed") &&
    !has(placements.instagramPositions, "instagramFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_INSTAGRAM_PROFILE_FEED_REQUIRED),
    });
  }
  if (
    has(placements.instagramPositions, "instagramSearchResults") &&
    !has(placements.instagramPositions, "instagramFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(
        FIELD_META_PLACEMENT_INSTAGRAM_SEARCH_RESULTS_REQUIRED
      ),
    });
  }
  if (
    has(placements.instagramPositions, "instagramExplore") &&
    !has(placements.instagramPositions, "instagramFeed")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_INSTAGRAM_EXPLORE_REQUIRED),
    });
  }
  // Instagram Explore home (special: requires both instagramFeed and instagramExplore)
  if (
    has(placements.instagramPositions, "instagramExploreHome") &&
    (!has(placements.instagramPositions, "instagramFeed") ||
      !has(placements.instagramPositions, "instagramExplore"))
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_INSTAGRAM_EXPLORE_HOME_REQUIRED),
    });
  }
  if (
    has(placements.instagramPositions, "instagramProfileReels") &&
    !has(placements.instagramPositions, "instagramReels")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "placements"],
      message: i18next.t(FIELD_META_PLACEMENT_INSTAGRAM_PROFILE_REELS_REQUIRED),
    });
  }
};
